import React, { FC, useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import { CircularProgress } from '@material-ui/core';
import EventDetailsContainer from '../EventDetails/EventDetailsContainer';
import {
  IBookingEvent,
  IEventAction,
  IRemindAppointmentDto,
  PersonAppointmentStatus,
} from 'modules/booking/interfaces';
import { PeakModules } from 'common/constants/peakModules';
import { SenderAvailabilityTypeList } from 'modules/booking/constants/senderAvailability';
import { EShortNotificationType } from 'modules/booking/constants/notificationType';
import { IFormParticipateEvent } from 'common/components/PersonProfile/components/Appointments/ParticipationModal/ParticipationModal';
import EventModal from './EventModal';

const useStyles = makeStyles(() => ({
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'absolute',
    left: 0,
    top: 0,
    right: 0,
    bottom: 0,
  },
}));

interface IEventDetailsModal {
  eventId?: string;
  eventDate?: string;
  personId?: number;
  readOnly?: boolean;
  anchorEl?: HTMLElement;
  eventDetails?: IBookingEvent;
  appointmentStatus?: PersonAppointmentStatus;
  isEventDetailsLoading?: boolean;
  isEventActionLoading?: boolean;
  onLoadEvent?: (eventId: string, eventDate: string) => void;
  onEdit?: (event: IBookingEvent) => void;
  onCancelParticipation?: (formData: IFormParticipateEvent) => void;
  onConfirmParticipation?: (formData: IFormParticipateEvent) => void;
  onJoinClass?: (formData: IFormParticipateEvent) => void;
  onClose: () => void;
  onDelete?: (formValues: IEventAction) => void;
  onCancel?: (formValues: IEventAction) => void;
  onRestore?: (event: IBookingEvent) => void;
  onRemind?: (formValues: IRemindAppointmentDto) => void;
  onResetEvent?: () => void;
  onDuplicateEvent?: (isDuplicateParticipants: boolean) => void;
  type: SenderAvailabilityTypeList;
  onLoadSenderAvailability: (
    type: SenderAvailabilityTypeList,
    shortTypeEvent: EShortNotificationType,
  ) => void;
  module?: PeakModules;
}

const EventDetailsModal: FC<IEventDetailsModal> = ({
  eventId,
  eventDate,
  personId,
  anchorEl,
  readOnly = false,
  eventDetails,
  isEventDetailsLoading,
  isEventActionLoading,
  onLoadEvent,
  onEdit,
  onClose,
  onDelete,
  onCancel,
  onRestore,
  onResetEvent,
  onDuplicateEvent,
  onRemind,
  type,
  appointmentStatus,
  onLoadSenderAvailability,
  onCancelParticipation,
  onConfirmParticipation,
  module,
  onJoinClass,
}) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const [isOverflow, setIsOverflow] = useState(false);

  useEffect(() => {
    if (anchorEl && eventId && eventDate && onLoadEvent) {
      onLoadEvent(eventId, eventDate);
    }

    return () => {
      if (onResetEvent) {
        onResetEvent();
      }
    };
  }, [eventId, eventDate, anchorEl, onLoadEvent, onResetEvent, readOnly, dispatch]);

  const handleOverflow = useCallback(overflow => {
    setIsOverflow(overflow);
  }, []);

  return (
    <EventModal isOpen={Boolean(anchorEl)} onClose={onClose} isOverflow={isOverflow}>
      {isEventDetailsLoading ? (
        <div className={classes.loadingContainer}>
          <CircularProgress color="primary" />
        </div>
      ) : (
        <EventDetailsContainer
          personId={personId}
          handleOverflow={handleOverflow}
          module={module}
          readOnly={readOnly}
          event={eventDetails}
          isEventDetailsLoading={isEventDetailsLoading}
          isEventActionLoading={isEventActionLoading}
          onEdit={onEdit}
          onClose={onClose}
          onDelete={onDelete}
          onCancel={onCancel}
          appointmentStatus={appointmentStatus}
          onCancelParticipation={onCancelParticipation}
          onConfirmParticipation={onConfirmParticipation}
          onRestore={onRestore}
          onDuplicate={onDuplicateEvent}
          onRemind={onRemind}
          type={type}
          onLoadSenderAvailability={onLoadSenderAvailability}
          onJoinClass={onJoinClass}
        />
      )}
    </EventModal>
  );
};

export default EventDetailsModal;
